import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScanLine, Camera, Upload, Loader2, CheckCircle2, X, Save, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api, { receptionsAPI } from '@/lib/api';

/** Calibration du format de facture d'un fournisseur : facture témoin →
 *  écran de contrôle champ par champ → profil mémorisé (injecté dans l'IA). */
export default function InvoiceCalibrationDialog({ open, onClose, supplier, sites = [], chain = null, onSkip = null, onSaved = null }) {
    const [step, setStep] = useState('setup'); // setup | extracting | review
    const [siteId, setSiteId] = useState('');
    const [file, setFile] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [progress, setProgress] = useState('');
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState(null);
    const [extracted, setExtracted] = useState(null); // first invoice of result
    const [form, setForm] = useState({});
    const cameraRef = useRef(null);
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailData, setEmailData] = useState(null); // {matched_folder, invoices}
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [aliases, setAliases] = useState([]);
    const [aliasesDirty, setAliasesDirty] = useState(false);
    const [newAlias, setNewAlias] = useState({ text: '', site_id: '' });

    useEffect(() => {
        if (open && supplier) {
            setStep('setup'); setSiteId(''); setFile(null); setPhotos([]); setExtracted(null); setForm({});
            setEmailData(null); setSelectedEmail(null); setEmailLoading(false);
            api.get(`/suppliers/${supplier.id}/invoice-profile`).then(r => {
                setProfile(r.data);
                setAliases(r.data.client_aliases || []);
                setAliasesDirty(false);
                setNewAlias({ text: '', site_id: '' });
                setForm({
                    invoice_number_pattern: r.data.invoice_number_pattern || '',
                    invoice_number_location: r.data.invoice_number_location || '',
                    date_format: r.data.date_format || '',
                    columns_order: r.data.columns_order || '',
                    default_vat: r.data.default_vat ?? '',
                    notes: r.data.notes || '',
                    paper_notes: r.data.paper_notes || '',
                });
            }).catch(() => { setProfile(null); setAliases([]); setAliasesDirty(false); });
        }
    }, [open, supplier]);

    const searchEmails = async () => {
        setEmailLoading(true);
        try {
            const res = await api.get(`/suppliers/${supplier.id}/email-invoices`);
            setEmailData(res.data);
            if (!res.data.matched_folder) toast.info('Aucun sous-dossier Factures correspondant à ce fournisseur');
            else if ((res.data.invoices || []).length === 0) toast.info('Aucun PDF trouvé dans ' + res.data.matched_folder);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de connexion à la boîte mail');
        } finally {
            setEmailLoading(false);
        }
    };

    const pollJob = async (jobId) => {
        const t0 = Date.now();
        while (Date.now() - t0 < 5 * 60 * 1000) {
            await new Promise(r => setTimeout(r, 2000));
            const st = await receptionsAPI.importInvoicePreviewStatus(jobId);
            setProgress(st.data.progress || '');
            if (st.data.status === 'done') {
                const inv = st.data.result?.invoices?.[0];
                if (!inv) { toast.error('Aucune facture détectée'); setStep('setup'); return; }
                setExtracted(inv);
                const vats = inv.items.map(it => it.vat_rate).filter(v => v != null);
                const domVat = vats.length ? vats.sort((a, b) => vats.filter(x => x === a).length - vats.filter(x => x === b).length).pop() : '';
                setForm(f => ({ ...f, default_vat: f.default_vat !== '' ? f.default_vat : domVat }));
                setStep('review');
                return;
            }
            if (st.data.status === 'error') { toast.error(st.data.error || 'Erreur d\'extraction'); setStep('setup'); return; }
        }
        toast.error('Délai dépassé'); setStep('setup');
    };

    const analyzeFromEmail = async () => {
        if (!siteId || !selectedEmail) { toast.error('Choisissez le site destinataire et une facture email'); return; }
        setStep('extracting');
        setProgress('Téléchargement de la facture depuis Gmail…');
        try {
            const start = await api.post(`/suppliers/${supplier.id}/invoice-profile/calibrate-from-email`, {
                folder: selectedEmail.folder, uid: selectedEmail.uid, filename: selectedEmail.filename, site_id: siteId,
            });
            await pollJob(start.data.job_id);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur'); setStep('setup');
        }
    };

    const analyze = async () => {
        if (!siteId || (!file && photos.length === 0)) {
            toast.error('Choisissez le site destinataire et une facture témoin');
            return;
        }
        setStep('extracting');
        setProgress('Envoi…');
        try {
            const fd = new FormData();
            if (photos.length > 0) photos.forEach(p => fd.append('photos', p));
            else fd.append('pdf', file);
            fd.append('supplier_id', supplier.id);
            fd.append('site_id', siteId);
            const start = await receptionsAPI.importInvoicePreview(fd);
            await pollJob(start.data.job_id);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur'); setStep('setup');
        }
    };

    const saveAliases = async () => {
        try {
            const res = await api.put(`/suppliers/${supplier.id}/invoice-profile`, { client_aliases: aliases });
            setProfile(res.data);
            setAliases(res.data.client_aliases || []);
            setAliasesDirty(false);
            toast.success('Alias clients mis à jour');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur d\'enregistrement des alias');
        }
    };

    const saveProfile = async () => {
        setSaving(true);
        try {
            const nextAliases = [...aliases];
            const cn = (extracted?.client_name || '').trim();
            if (cn && !nextAliases.some(a => (a.text || '').toLowerCase() === cn.toLowerCase())) {
                nextAliases.push({ text: cn, site_id: siteId });
            }
            await api.put(`/suppliers/${supplier.id}/invoice-profile`, {
                invoice_number_pattern: form.invoice_number_pattern || null,
                invoice_number_location: form.invoice_number_location || null,
                date_format: form.date_format || null,
                columns_order: form.columns_order || null,
                default_vat: form.default_vat === '' ? null : parseFloat(form.default_vat),
                notes: form.notes || null,
                paper_notes: form.paper_notes || null,
                client_aliases: nextAliases,
                add_invoice_number_example: extracted?.invoice_number || null,
            });
            toast.success('Profil de facture mémorisé — il sera utilisé aux prochains imports');
            if (onSaved) onSaved(); else onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur d\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    if (!supplier) return null;
    const F = ({ label, field, placeholder }) => (
        <div className="space-y-1">
            <Label className="text-xs">{label}</Label>
            <Input
                value={form[field] ?? ''}
                onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}
                placeholder={placeholder}
                className="h-8 text-sm"
                data-testid={`calib-${field}`}
            />
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-calibration-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ScanLine className="w-5 h-5 text-orange-500" /> Calibrer le format de facture — {supplier.name}
                        {chain && (
                            <span className="ml-auto text-xs font-bold bg-orange-100 text-orange-700 rounded-full px-2.5 py-1" data-testid="chain-progress">
                                {chain.remaining} restant(s)
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        Analysez une facture témoin, contrôlez les champs détectés, puis mémorisez le format.
                        Ces indices seront injectés dans l'IA à chaque import de ce fournisseur.
                    </DialogDescription>
                </DialogHeader>

                {step === 'setup' && (
                    <div className="space-y-3">
                        {profile?.calibrated_at && (
                            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-700">
                                Profil déjà calibré le {new Date(profile.calibrated_at).toLocaleDateString('fr-FR')} — refaire un test le mettra à jour.
                            </div>
                        )}
                        <div className="text-xs bg-slate-50 border rounded-lg px-3 py-2 space-y-2" data-testid="calib-aliases-block">
                            <div className="font-semibold text-slate-600">Alias clients (libellé facture → site) :</div>
                            {aliases.length === 0 && <div className="text-slate-400">Aucun alias mémorisé pour ce fournisseur.</div>}
                            {aliases.map((a, i) => (
                                <div key={i} className="flex items-center gap-1.5" data-testid={`calib-alias-row-${i}`}>
                                    <Input value={a.text} onChange={(e) => { setAliases(prev => prev.map((x, j) => j === i ? { ...x, text: e.target.value } : x)); setAliasesDirty(true); }}
                                        className="h-7 text-xs flex-1 min-w-0" data-testid={`calib-alias-text-${i}`} />
                                    <select value={a.site_id || ''} onChange={(e) => { setAliases(prev => prev.map((x, j) => j === i ? { ...x, site_id: e.target.value } : x)); setAliasesDirty(true); }}
                                        className="h-7 text-xs border border-input rounded-md px-1.5 bg-background w-28 shrink-0" data-testid={`calib-alias-site-${i}`}>
                                        <option value="" disabled>Site…</option>
                                        {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                    <button type="button" onClick={() => { setAliases(prev => prev.filter((_, j) => j !== i)); setAliasesDirty(true); }}
                                        className="text-red-400 hover:text-red-600 shrink-0 p-1" data-testid={`calib-alias-delete-${i}`}>
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                            <div className="flex items-center gap-1.5 pt-1 border-t">
                                <Input value={newAlias.text} onChange={(e) => setNewAlias(n => ({ ...n, text: e.target.value }))}
                                    placeholder="Libellé client sur la facture…" className="h-7 text-xs flex-1 min-w-0" data-testid="calib-alias-new-text" />
                                <select value={newAlias.site_id} onChange={(e) => setNewAlias(n => ({ ...n, site_id: e.target.value }))}
                                    className="h-7 text-xs border border-input rounded-md px-1.5 bg-background w-28 shrink-0" data-testid="calib-alias-new-site">
                                    <option value="" disabled>Site…</option>
                                    {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                                <Button type="button" size="sm" variant="outline" className="h-7 text-xs px-2 shrink-0"
                                    disabled={!newAlias.text.trim() || !newAlias.site_id}
                                    onClick={() => { setAliases(prev => [...prev, { text: newAlias.text.trim(), site_id: newAlias.site_id }]); setNewAlias({ text: '', site_id: '' }); setAliasesDirty(true); }}
                                    data-testid="calib-alias-add-btn">+</Button>
                            </div>
                            {aliasesDirty && (
                                <Button type="button" size="sm" onClick={saveAliases} className="h-7 text-xs btn-primary w-full" data-testid="calib-alias-save-btn">
                                    <Save className="w-3 h-3 mr-1" /> Enregistrer les alias
                                </Button>
                            )}
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Cette facture témoin est adressée à quel site ? *</Label>
                            <Select value={siteId} onValueChange={setSiteId}>
                                <SelectTrigger className="h-9" data-testid="calib-site-select"><SelectValue placeholder="Choisir un site" /></SelectTrigger>
                                <SelectContent>
                                    {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="border rounded-lg p-3 space-y-2 bg-slate-50/60">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                                    <Mail className="w-3.5 h-3.5 text-orange-500" /> Factures reçues par email
                                    {emailData?.matched_folder && <span className="text-slate-400 font-normal">({emailData.matched_folder})</span>}
                                </span>
                                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={searchEmails} disabled={emailLoading} data-testid="calib-search-emails-btn">
                                    {emailLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                                    {emailLoading ? 'Recherche…' : (emailData ? 'Actualiser' : 'Chercher dans les emails')}
                                </Button>
                            </div>
                            {emailData && (emailData.invoices || []).length > 0 && (
                                <div className="max-h-44 overflow-y-auto divide-y border rounded bg-white" data-testid="calib-email-list">
                                    {emailData.invoices.map((inv, i) => (
                                        <button
                                            key={`${inv.uid}-${i}`}
                                            type="button"
                                            onClick={() => setSelectedEmail(inv)}
                                            className={`w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-2 ${selectedEmail === inv ? 'bg-orange-50 ring-1 ring-inset ring-orange-300' : 'hover:bg-slate-50'}`}
                                            data-testid={`calib-email-${i}`}
                                        >
                                            <span className="font-mono text-slate-400 w-16 shrink-0">{inv.date}</span>
                                            <span className="flex-1 truncate font-medium text-slate-700">{inv.filename}</span>
                                            <span className="text-slate-400 shrink-0">{inv.size_kb} Ko</span>
                                            {selectedEmail === inv && <CheckCircle2 className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {selectedEmail && (
                                <div className="flex justify-end">
                                    <Button type="button" size="sm" onClick={analyzeFromEmail} disabled={!siteId} className="btn-primary h-8" data-testid="calib-analyze-email-btn">
                                        <Upload className="w-3.5 h-3.5 mr-1.5" /> Analyser cette facture email
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Input
                                type="file"
                                accept="application/pdf,image/*"
                                className="flex-1"
                                onChange={(e) => {
                                    const f = e.target.files?.[0] || null;
                                    if (f && f.type.startsWith('image/')) { setPhotos(p => [...p, f]); setFile(null); e.target.value = ''; }
                                    else { setFile(f); setPhotos([]); }
                                }}
                                data-testid="calib-file-input"
                            />
                            <Button type="button" variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50" onClick={() => cameraRef.current?.click()} data-testid="calib-camera-btn">
                                <Camera className="w-4 h-4 mr-2" /> {photos.length > 0 ? 'Ajouter une page' : 'Prendre en photo'}
                            </Button>
                            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPhotos(p => [...p, f]); setFile(null); } e.target.value = ''; }} />
                        </div>
                        {photos.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                                {photos.map((p, i) => (
                                    <div key={i} className="relative border rounded overflow-hidden">
                                        <img src={URL.createObjectURL(p)} alt={`p${i}`} className="h-16 w-14 object-cover" />
                                        <button type="button" onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/60 text-white p-0.5"><X className="w-3 h-3" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex justify-between items-center">
                            {chain && onSkip ? (
                                <Button variant="ghost" className="text-slate-500" onClick={onSkip} data-testid="calib-skip-btn">
                                    Passer ce fournisseur →
                                </Button>
                            ) : <span />}
                            <Button onClick={analyze} disabled={!siteId || (!file && photos.length === 0)} className="btn-primary" data-testid="calib-analyze-btn">
                                <Upload className="w-4 h-4 mr-2" /> Analyser la facture témoin
                            </Button>
                        </div>
                    </div>
                )}

                {step === 'extracting' && (
                    <div className="flex flex-col items-center gap-3 py-10">
                        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                        <div className="text-sm text-slate-500">{progress}</div>
                    </div>
                )}

                {step === 'review' && extracted && (
                    <div className="space-y-3" data-testid="calib-review">
                        <div className="grid grid-cols-2 gap-2 text-sm border rounded-lg p-3 bg-slate-50">
                            <div><span className="text-slate-500 text-xs">N° de pièce détecté</span><div className="font-mono font-semibold">{extracted.invoice_number || '— non détecté'}</div></div>
                            <div><span className="text-slate-500 text-xs">Date détectée</span><div className="font-semibold">{extracted.invoice_date || '— non détectée'}</div></div>
                            <div><span className="text-slate-500 text-xs">Client détecté</span><div className="font-semibold">{extracted.client_name || '— non détecté'}</div></div>
                            <div><span className="text-slate-500 text-xs">Lignes produits</span><div className="font-semibold">{extracted.items?.length || 0} ligne(s) <span className="text-slate-400 font-normal">· Total {extracted.total_ht} € HT</span></div></div>
                        </div>
                        {extracted.client_name && (
                            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-1.5">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                « {extracted.client_name} » sera associé à {sites.find(s => s.id === siteId)?.name}
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <F label="Motif du n° de pièce" field="invoice_number_pattern" placeholder='ex : commence par "FC" + 5 chiffres' />
                            <F label="Emplacement du n° de pièce" field="invoice_number_location" placeholder="ex : en haut à droite" />
                            <F label="Format de date" field="date_format" placeholder="ex : JJ/MM/AAAA" />
                            <F label="Ordre des colonnes produits" field="columns_order" placeholder="ex : Réf | Désignation | Qté | PU HT | TVA | Total" />
                            <F label="TVA habituelle (%)" field="default_vat" placeholder="8.5" />
                            <F label="Notes / autres indices pour l'IA" field="notes" placeholder="ex : les remises sont en bas, ignorer la consigne" />
                            <F label="Calibrage format papier (photo/scan)" field="paper_notes" placeholder="ex : sur la facture papier, les colonnes sont Réf | Désignation | Colis | PU ; le n° est en bas à gauche" />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setStep('setup')}>Retour</Button>
                            <Button onClick={saveProfile} disabled={saving} className="btn-primary" data-testid="calib-save-btn">
                                <Save className="w-4 h-4 mr-2" /> {saving ? 'Enregistrement…' : 'Mémoriser ce format'}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
